import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  InputBase,
  Paper,
  Select,
  MenuItem,
  Menu,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useBrand } from '../../contexts/BrandContext';
import {
  fetchConversations,
  fetchConversationMessages,
  markConversationAsRead,
  sendOutboundMessage,
  uploadConversationMedia,
  fetchWhatsappTemplates,
  fetchMauticSegments,
  fetchMauticContactsBySegment,
  launchWhatsappTemplateCampaign,
  fetchWhatsappCampaignHistory,
} from '../../services/inboxClient';

const CHANNELS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'messenger', label: 'Messenger' },
  { value: 'instagram', label: 'Instagram' },
];

function formatTs(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  return d.toLocaleString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
  });
}

/** Affiche la date/heure de lancement planifiée (API en UTC → heure locale du navigateur). */
function formatScheduledLaunchDisplay(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  return d.toLocaleString('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function normalizeForSend(channel, userId) {
  const uid = userId ?? '';
  if (String(channel || '').toLowerCase() === 'whatsapp') return uid.replace(/^\+/, '');
  return uid;
}

function parseConversationId(conversationId) {
  const parts = String(conversationId || '').split(':');
  if (parts.length >= 2) {
    return { channel: parts[0], userId: parts.slice(1).join(':') };
  }
  return { userId: conversationId };
}

export default function ClientDiscussions() {
  const { currentBrand, loading: brandLoading } = useBrand();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [channel, setChannel] = useState('whatsapp');
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState('');
  const [messages, setMessages] = useState([]);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [convSearch, setConvSearch] = useState('');
  const [mobileView, setMobileView] = useState('list'); // list | chat
  const [conversationsLimit, setConversationsLimit] = useState(50);
  const [messagesLimit, setMessagesLimit] = useState(50);

  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [selectedImagePreview, setSelectedImagePreview] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [attachMenuEl, setAttachMenuEl] = useState(null);
  const [emojiMenuEl, setEmojiMenuEl] = useState(null);
  const [fullscreenImageUrl, setFullscreenImageUrl] = useState('');
  const [error, setError] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [selectedTemplateName, setSelectedTemplateName] = useState('');
  const [mauticSegments, setMauticSegments] = useState([]);
  const [loadingMauticSegments, setLoadingMauticSegments] = useState(false);
  const [campaignSegmentValue, setCampaignSegmentValue] = useState('');
  const [launchingCampaign, setLaunchingCampaign] = useState(false);
  const [campaignResult, setCampaignResult] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [campaignHistory, setCampaignHistory] = useState([]);
  const [loadingCampaignHistory, setLoadingCampaignHistory] = useState(false);

  const draftRef = useRef(null);
  const endRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);
  const mediaInputRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const conversationsLimitRef = useRef(50);
  const messagesLimitRef = useRef(50);
  const selectedConversationIdRef = useRef('');

  // Keep refs in sync with state values
  conversationsLimitRef.current = conversationsLimit;
  messagesLimitRef.current = messagesLimit;
  selectedConversationIdRef.current = selectedConversationId;

  const EMOJIS = ['😀', '😂', '😍', '👍', '🙏', '🔥', '🎉', '😢', '❤️', '👌'];

  const selectedConversation = useMemo(() => {
    return conversations.find((c) => c.id === selectedConversationId);
  }, [conversations, selectedConversationId]);

  const selectedTemplate = useMemo(() => {
    if (!selectedTemplateName) return null;
    return whatsappTemplates.find((t) => t?.name === selectedTemplateName) || null;
  }, [whatsappTemplates, selectedTemplateName]);

  const selectedCampaignSegment = useMemo(() => {
    if (!campaignSegmentValue) return null;
    return mauticSegments.find((s) => String(s?.id || '') === String(campaignSegmentValue)) || null;
  }, [mauticSegments, campaignSegmentValue]);

  const filteredConversations = useMemo(() => {
    const q = String(convSearch || '').trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) => {
      const a = String(c.userId || c.id || '').toLowerCase();
      const b = String(c.lastMessage || '').toLowerCase();
      return a.includes(q) || b.includes(q);
    });
  }, [conversations, convSearch]);

  async function loadConversations({ silent = false, limitOverride } = {}) {
    if (!currentBrand?.name) return;

    if (!silent) {
      setLoadingConversations(true);
      setError('');
    }

    const currentLimit = limitOverride ?? conversationsLimitRef.current;

    try {
      const list = await fetchConversations({
        brand: currentBrand.name,
        channel,
        limit: currentLimit,
      });

      setConversations(list);

      // Auto-select if needed (or if selected conversation disappeared)
      const activeId = selectedConversationIdRef.current;
      const stillExists = list.some((c) => c.id === activeId);
      if (!activeId || !stillExists) {
        setSelectedConversationId(list[0]?.id || '');
      }
    } catch (e) {
      if (!silent) setError(e?.message || String(e));
      // Avoid noisy UI errors during polling
      if (silent) console.error('[CLIENT-DISCUSSIONS] polling conversations error:', e);
    } finally {
      if (!silent) setLoadingConversations(false);
    }
  }

  async function loadMessages(conversationIdValue, { silent = false, limitOverride } = {}) {
    if (!conversationIdValue) return;

    if (!silent) {
      setLoadingMessages(true);
      setError('');
    }

    const queryLimit = silent ? 50 : (limitOverride ?? messagesLimitRef.current);

    try {
      const { messages: list, hasMore } = await fetchConversationMessages({
        conversationId: conversationIdValue,
        limit: queryLimit,
      });

      if (!silent) {
        setMessages(list);
        setHasMoreMessages(hasMore);
      } else {
        // Silent poll: merge NEW messages at the bottom (dedup by id)
        setMessages((prev) => {
          if (!list || list.length === 0) return prev;
          const existingIds = new Set(prev.map((m) => m.id));
          const newOnes = list.filter((m) => !existingIds.has(m.id));
          if (newOnes.length === 0) return prev;
          return [...prev, ...newOnes];
        });
        // Do NOT touch hasMoreMessages during polling
      }

      try {
        await markConversationAsRead(conversationIdValue);
      } catch (_e) {
        // non blocking
      }
    } catch (e) {
      if (!silent) setError(e?.message || String(e));
      if (silent) console.error('[CLIENT-DISCUSSIONS] polling messages error:', e);
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  }

  /**
   * Loads older messages by increasing the messages limit.
   */
  async function loadMoreMessages() {
    if (!selectedConversationId || loadingMoreMessages || !hasMoreMessages) return;
    setLoadingMoreMessages(true);
    setError('');

    const newLimit = messagesLimitRef.current + 50;
    setMessagesLimit(newLimit);

    try {
      const { messages: list, hasMore } = await fetchConversationMessages({
        conversationId: selectedConversationId,
        limit: newLimit,
      });

      // Preserve scroll position: remember current scrollTop and scrollHeight
      const container = messagesContainerRef.current;
      const prevScrollHeight = container ? container.scrollHeight : 0;

      setMessages(list);
      setHasMoreMessages(hasMore);

      // Restore scroll so the user stays at the same visual position.
      if (container) {
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollTop + (container.scrollHeight - prevScrollHeight);
        });
      }
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoadingMoreMessages(false);
    }
  }

  // Poll conversations
  useEffect(() => {
    if (!currentBrand?.name) return;

    let mounted = true;
    setConversationsLimit(50);
    loadConversations({ silent: false, limitOverride: 50 });

    const t = window.setInterval(() => {
      if (!mounted) return;
      loadConversations({ silent: true });
    }, 8000);

    return () => {
      mounted = false;
      window.clearInterval(t);
    };
  }, [currentBrand?.name, channel]);

  useEffect(() => {
    let cancelled = false;
    async function loadWhatsappTemplates() {
      if (channel !== 'whatsapp' || !currentBrand?.name) {
        setWhatsappTemplates([]);
        setSelectedTemplateName('');
        return;
      }
      setLoadingTemplates(true);
      try {
        const list = await fetchWhatsappTemplates({ brand: currentBrand.name });
        if (cancelled) return;
        setWhatsappTemplates(Array.isArray(list) ? list : []);
      } catch (e) {
        if (cancelled) return;
        setWhatsappTemplates([]);
        setSelectedTemplateName('');
        setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoadingTemplates(false);
      }
    }
    loadWhatsappTemplates();
    return () => {
      cancelled = true;
    };
  }, [channel, currentBrand?.name]);

  useEffect(() => {
    let cancelled = false;
    async function loadMauticSegments() {
      if (String(channel || '').toLowerCase() !== 'whatsapp') {
        setMauticSegments([]);
        setCampaignSegmentValue('');
        return;
      }
      setLoadingMauticSegments(true);
      try {
        const list = await fetchMauticSegments();
        if (cancelled) return;
        setMauticSegments(Array.isArray(list) ? list : []);
      } catch (e) {
        if (cancelled) return;
        setMauticSegments([]);
        setCampaignSegmentValue('');
        setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoadingMauticSegments(false);
      }
    }
    loadMauticSegments();
    return () => {
      cancelled = true;
    };
  }, [channel]);

  useEffect(() => {
    if (!campaignModalOpen || !currentBrand?.name || String(channel || '').toLowerCase() !== 'whatsapp') return undefined;
    const t = window.setInterval(async () => {
      try {
        const history = await fetchWhatsappCampaignHistory({ brand: currentBrand.name, limit: 20 });
        setCampaignHistory(Array.isArray(history) ? history : []);
      } catch (_e) {
        // no-op
      }
    }, 5000);
    return () => window.clearInterval(t);
  }, [campaignModalOpen, currentBrand?.name, channel]);

  function buildTemplateSendPayload(templateObj) {
    if (!templateObj?.name) return {};
    const languageCode = String(templateObj?.language || 'en');
    const hasFlowButton = Array.isArray(templateObj?.components)
      && templateObj.components.some(
        (comp) =>
          String(comp?.type || '').toUpperCase() === 'BUTTONS'
          && Array.isArray(comp?.buttons)
          && comp.buttons.some((b) => String(b?.type || '').toUpperCase() === 'FLOW'),
      );

    if (!hasFlowButton) {
      return {
        templateName: templateObj.name,
        templateLanguage: languageCode,
      };
    }

    return {
      templateName: templateObj.name,
      templateLanguage: languageCode,
      templateComponents: [
        {
          type: 'button',
          sub_type: 'flow',
          index: '0',
        },
      ],
    };
  }

  // Poll messages for selected conversation
  useEffect(() => {
    if (!selectedConversationId) return;

    let mounted = true;
    loadMessages(selectedConversationId, { silent: false });

    const t = window.setInterval(() => {
      if (!mounted) return;
      loadMessages(selectedConversationId, { silent: true });
    }, 3500);

    return () => {
      mounted = false;
      window.clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversationId]);

  // Auto-scroll only when user stays near bottom
  useEffect(() => {
    if (!shouldAutoScrollRef.current) return;
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  function handleMessagesScroll(e) {
    const el = e?.currentTarget;
    if (!el) return;
    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    shouldAutoScrollRef.current = distanceToBottom < 80;
  }

  async function onSelectConversation(c) {
    setSelectedConversationId(c?.id || '');
    setMessages([]);
    setHasMoreMessages(false);
    setMessagesLimit(50);
    shouldAutoScrollRef.current = true;
    await loadMessages(c?.id, { silent: false, limitOverride: 50 });
    if (isMobile) setMobileView('chat');
  }

  async function onSend() {
    if (sending) return;
    setError('');

    const textarea = draftRef.current;
    const text = (textarea?.value || '').trim();
    const channelToSendCandidate = selectedConversation?.channel || channel;
    const isWhatsappSend = String(channelToSendCandidate || '').toLowerCase() === 'whatsapp';
    const willSendTemplate = isWhatsappSend && Boolean(selectedTemplate);
    if (!text && !selectedImageFile && !selectedFile && !willSendTemplate) return;

    if (!selectedConversation) {
      setError('Choisis une conversation d abord.');
      return;
    }

    const parsed = selectedConversation?.userId
      ? { userId: selectedConversation.userId }
      : parseConversationId(selectedConversation.id);

    const channelToSend = selectedConversation.channel || channel;
    const brandToSend = selectedConversation.brand || currentBrand?.name;
    const userIdForSend = normalizeForSend(channelToSend, parsed.userId);

    if (!brandToSend || !userIdForSend) {
      setError('Impossible d identifier la marque ou le contact pour envoyer.');
      return;
    }

    setSending(true);
    try {
      let uploadedImageUrl = '';
      let uploadedFileUrl = '';
      let uploadedFileName = '';
      if (selectedImageFile) {
        const uploadResp = await uploadConversationMedia(selectedImageFile);
        uploadedImageUrl = uploadResp?.url || '';
        if (!uploadedImageUrl) {
          throw new Error("L'image a ete selectionnee mais l'upload a echoue.");
        }
      }
      if (selectedFile) {
        const uploadResp = await uploadConversationMedia(selectedFile);
        uploadedFileUrl = uploadResp?.url || '';
        uploadedFileName = uploadResp?.filename || selectedFile.name;
        if (!uploadedFileUrl) {
          throw new Error("Le fichier a ete selectionne mais l'upload a echoue.");
        }
      }

      await sendOutboundMessage({
        channel: channelToSend,
        brand: brandToSend,
        userId: userIdForSend,
        message: text,
        imageUrl: uploadedImageUrl || undefined,
        fileUrl: uploadedFileUrl || undefined,
        fileName: uploadedFileName || undefined,
        contactName: selectedConversation?.contactName || selectedConversation?.displayName || undefined,
        ...(willSendTemplate ? buildTemplateSendPayload(selectedTemplate) : {}),
      });

      // Clear + refresh
      if (draftRef.current) draftRef.current.value = '';
      setSelectedImageFile(null);
      setSelectedImagePreview('');
      setSelectedFile(null);
      if (mediaInputRef.current) mediaInputRef.current.value = '';
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (willSendTemplate) setSelectedTemplateName('');
      await loadConversations({ silent: true });
      await loadMessages(selectedConversation.id, { silent: true });
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSending(false);
    }
  }

  async function onLaunchWhatsappCampaign() {
    if (launchingCampaign) return;
    setError('');
    setCampaignResult(null);

    if (String(channel || '').toLowerCase() !== 'whatsapp') {
      setError('La campagne WhatsApp est disponible uniquement quand le canal WhatsApp est sélectionné.');
      return;
    }
    if (!currentBrand?.name) {
      setError('Brand active introuvable.');
      return;
    }
    if (!selectedTemplate) {
      setError('Sélectionne un template WhatsApp avant de lancer la campagne.');
      return;
    }
    if (!selectedCampaignSegment) {
      setError('Sélectionne un segment Mautic.');
      return;
    }

    setLaunchingCampaign(true);
    try {
      const contactsPayload = await fetchMauticContactsBySegment({
        segmentName: selectedCampaignSegment.name || '',
        segmentAlias: selectedCampaignSegment.alias || '',
      });
      const templatePayload = buildTemplateSendPayload(selectedTemplate);
      const resp = await launchWhatsappTemplateCampaign({
        brand: currentBrand.name,
        templateName: templatePayload.templateName || selectedTemplate.name,
        templateLanguage: templatePayload.templateLanguage || selectedTemplate.language || 'en',
        templateComponents: templatePayload.templateComponents,
        contactsPayload,
      });
      setCampaignResult(resp);
      const history = await fetchWhatsappCampaignHistory({ brand: currentBrand.name, limit: 20 });
      setCampaignHistory(Array.isArray(history) ? history : []);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLaunchingCampaign(false);
    }
  }

  async function openCampaignModal() {
    if (String(channel || '').toLowerCase() !== 'whatsapp') {
      setError('Passe sur le canal WhatsApp pour gerer les campagnes.');
      return;
    }
    setCampaignModalOpen(true);
    setError('');
    setLoadingCampaignHistory(true);
    try {
      const history = await fetchWhatsappCampaignHistory({ brand: currentBrand?.name, limit: 20 });
      setCampaignHistory(Array.isArray(history) ? history : []);
    } catch (e) {
      setError(e?.message || String(e));
      setCampaignHistory([]);
    } finally {
      setLoadingCampaignHistory(false);
    }
  }

  function onOpenAttachMenu(e) {
    setAttachMenuEl(e.currentTarget);
  }

  function onCloseAttachMenu() {
    setAttachMenuEl(null);
  }

  function onOpenEmojiMenu(e) {
    setEmojiMenuEl(e.currentTarget);
  }

  function onCloseEmojiMenu() {
    setEmojiMenuEl(null);
  }

  function onPickEmoji(emoji) {
    const textarea = draftRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart ?? textarea.value.length;
    const end = textarea.selectionEnd ?? textarea.value.length;
    const before = textarea.value.slice(0, start);
    const after = textarea.value.slice(end);
    textarea.value = `${before}${emoji}${after}`;
    const nextPos = start + emoji.length;
    textarea.focus();
    textarea.setSelectionRange(nextPos, nextPos);
    onCloseEmojiMenu();
  }

  function onPickImageClick() {
    onCloseAttachMenu();
    mediaInputRef.current?.click();
  }

  function onPickFileClick() {
    onCloseAttachMenu();
    fileInputRef.current?.click();
  }

  function onSelectImageFile(e) {
    const file = e?.target?.files?.[0];
    if (!file) return;

    if (!String(file.type || '').startsWith('image/')) {
      setError("Le fichier selectionne n'est pas une image.");
      if (mediaInputRef.current) mediaInputRef.current.value = '';
      return;
    }

    const maxBytes = 10 * 1024 * 1024;
    if (file.size > maxBytes) {
      setError('Image trop volumineuse (max 10MB).');
      if (mediaInputRef.current) mediaInputRef.current.value = '';
      return;
    }

    setError('');
    setSelectedImageFile(file);
    setSelectedFile(null);
    setSelectedImagePreview(URL.createObjectURL(file));
  }

  function onSelectFile(e) {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const maxBytes = 20 * 1024 * 1024;
    if (file.size > maxBytes) {
      setError('Fichier trop volumineux (max 20MB).');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setError('');
    setSelectedFile(file);
    setSelectedImageFile(null);
    setSelectedImagePreview('');
  }

  function removeSelectedImage() {
    setSelectedImageFile(null);
    setSelectedImagePreview('');
    if (mediaInputRef.current) mediaInputRef.current.value = '';
  }

  function removeSelectedFile() {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  useEffect(() => {
    return () => {
      if (selectedImagePreview) URL.revokeObjectURL(selectedImagePreview);
    };
  }, [selectedImagePreview]);

  // Keep a sensible mobile view when selection changes.
  useEffect(() => {
    if (!isMobile) return;
    if (selectedConversationId) setMobileView('chat');
    else setMobileView('list');
  }, [isMobile, selectedConversationId]);

  function initialsFrom(value) {
    const s = String(value || '').replace(/[^\w\s]/g, '').trim();
    if (!s) return 'C';
    const parts = s.split(/\s+/).filter(Boolean);
    const first = (parts[0] || 'C')[0] || 'C';
    const last = (parts[1] || '')[0] || '';
    return (first + last).toUpperCase();
  }

  function isInbound(m) {
    return String(m?.direction || '').toLowerCase() === 'inbound';
  }

  function statusIconForMessage(m) {
    const s = String(m?.status || '').toLowerCase();
    if (s === 'delivered' || s === 'read') {
      return <DoneAllIcon sx={{ fontSize: 14, color: s === 'read' ? '#2563eb' : '#6b7280' }} />;
    }
    if (s === 'sent' || s === 'stored_in_unomi') {
      return <DoneIcon sx={{ fontSize: 14, color: '#6b7280' }} />;
    }
    return null;
  }

  function isLocalInboxMediaUrl(url) {
    return String(url || '').includes('/api/inbox/media/');
  }

  function isMediaReady(m, url) {
    if (!url) return false;
    if (!isInbound(m)) return true;
    return isLocalInboxMediaUrl(url);
  }

  if (brandLoading) {
    return (
      <Box
        sx={{
          height: 'calc(100vh - 100px)',
          maxHeight: 'calc(100vh - 100px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!currentBrand?.name) {
    return (
      <Box
        sx={{
          height: 'calc(100vh - 100px)',
          maxHeight: 'calc(100vh - 100px)',
          p: 2,
          overflow: 'hidden',
        }}
      >
        <Alert severity="warning">
          Aucune marque active. Sélectionne une brand via le menu en haut.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: 'calc(100vh - 100px)',
        maxHeight: 'calc(100vh - 100px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        minHeight: 0,
        overflow: 'hidden', // Pas de scroll global sur la page
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 3,
          border: '1px solid #e9ecef',
          background:
            'linear-gradient(135deg, rgba(37,99,235,0.10) 0%, rgba(16,185,129,0.06) 45%, rgba(255,255,255,1) 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 42,
                height: 42,
                fontWeight: 900,
                bgcolor: 'rgba(37,99,235,0.15)',
                color: '#1d4ed8',
                border: '1px solid rgba(37,99,235,0.25)',
              }}
            >
              {initialsFrom(currentBrand.brand_label || currentBrand.name)}
            </Avatar>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <Typography sx={{ fontWeight: 900, fontSize: 18, fontFamily: 'Montserrat, Arial' }}>
                Discussions clients
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                Brand active : <b>{currentBrand.brand_label || currentBrand.name}</b>
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip
              label={channel}
              variant="outlined"
              sx={{ fontWeight: 900, bgcolor: 'rgba(255,255,255,0.85)' }}
            />

            <Select
              size="small"
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              sx={{
                bgcolor: 'rgba(255,255,255,0.85)',
                minWidth: 190,
                borderRadius: 2,
                fontWeight: 800,
              }}
            >
              {CHANNELS.map((ch) => (
                <MenuItem key={ch.value} value={ch.value}>
                  {ch.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          flex: 1,
          minHeight: 0,
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid #e9ecef',
          background: '#fff',
          display: 'flex',
        }}
      >
        {/* Conversations */}
        <Box
          sx={{
            width: { xs: '100%', md: 380 },
            borderRight: '1px solid #e9ecef',
            display: isMobile ? (mobileView === 'list' ? 'flex' : 'none') : 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid #e9ecef', bgcolor: '#f8fafc' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 900, fontSize: 14 }}>Conversations</Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: 12, mt: 0.4 }}>
                  {loadingConversations ? 'Chargement...' : `${conversations.length} conversation(s)`}
                </Typography>
              </Box>
              
            </Box>

            <Box
              sx={{
                mt: 1.25,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                border: '1px solid #e5e7eb',
                borderRadius: 2,
                px: 1.25,
                py: 0.5,
                bgcolor: '#fff',
              }}
            >
              <SearchIcon fontSize="small" style={{ color: '#6b7280' }} />
              <InputBase
                value={convSearch}
                onChange={(e) => setConvSearch(e.target.value)}
                placeholder="Rechercher un contact ou message..."
                sx={{ flex: 1, fontSize: 13, fontWeight: 700 }}
              />
              {convSearch ? (
                <Tooltip title="Effacer">
                  <IconButton size="small" onClick={() => setConvSearch('')}>
                    <span style={{ fontSize: 16, lineHeight: 1 }}>×</span>
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>
          </Box>

          <Box sx={{ p: 1.5, overflow: 'auto' }}>
            {error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : null}

            {loadingConversations && conversations.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : null}

            {filteredConversations.map((c) => {
              const active = c.id === selectedConversationId;
              const title = c.displayName ? String(c.displayName) : (c.userId ? String(c.userId) : String(c.id));
              const lastDir = String(c.lastDirection || '').toLowerCase();
              const badgeColor = lastDir === 'inbound' ? '#10B981' : lastDir === 'outbound' ? '#2563eb' : '#6b7280';

              return (
                <Box
                  key={c.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelectConversation(c)}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 2.5,
                    p: 1.4,
                    mb: 1,
                    border: active ? '1px solid rgba(37, 99, 235, 0.35)' : '1px solid #f3f4f6',
                    background: active ? 'rgba(37, 99, 235, 0.07)' : '#fff',
                    boxShadow: active ? '0 8px 24px rgba(37,99,235,0.10)' : 'none',
                    '&:hover': { background: '#f1f7ff' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Avatar
                      sx={{
                        width: 34,
                        height: 34,
                        bgcolor: active ? 'rgba(37,99,235,0.15)' : '#f3f4f6',
                        color: active ? '#1d4ed8' : '#374151',
                        fontWeight: 900,
                        border: active ? '1px solid rgba(37,99,235,0.25)' : '1px solid #e5e7eb',
                      }}
                    >
                      {initialsFrom(title)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'baseline' }}>
                        <Typography sx={{ fontWeight: 900, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {title}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 800 }}>
                          {formatTs(c.lastTimestamp)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 0.4 }}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: '#111827',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {c.lastMessage}
                        </Typography>
                        {c.unreadCount > 0 ? (
                          <Chip
                            size="small"
                            label={c.unreadCount}
                            sx={{
                              height: 18,
                              minWidth: 18,
                              fontSize: 10,
                              fontWeight: 900,
                              bgcolor: '#10B981',
                              color: '#fff',
                            }}
                          />
                        ) : c.lastDirection ? (
                          <Chip
                            size="small"
                            label={String(c.lastDirection).toUpperCase()}
                            sx={{
                              height: 18,
                              fontSize: 10,
                              fontWeight: 900,
                              bgcolor: `${badgeColor}1A`,
                              color: badgeColor,
                              border: `1px solid ${badgeColor}33`,
                            }}
                          />
                        ) : null}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}

            {!loadingConversations && filteredConversations.length === 0 ? (
              <Typography sx={{ color: 'text.secondary', fontSize: 13, p: 2, textAlign: 'center' }}>
                Aucune conversation trouvée.
              </Typography>
            ) : null}

            {conversations.length >= conversationsLimit ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={async () => {
                    const newLimit = conversationsLimit + 50;
                    setConversationsLimit(newLimit);
                    await loadConversations({ silent: false, limitOverride: newLimit });
                  }}
                  disabled={loadingConversations}
                  startIcon={loadingConversations ? <CircularProgress size={12} /> : null}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 800,
                    fontSize: 11,
                    textTransform: 'none',
                    borderColor: 'rgba(37,99,235,0.3)',
                    color: '#2563eb',
                    '&:hover': {
                      bgcolor: 'rgba(37,99,235,0.05)',
                      borderColor: '#2563eb',
                    }
                  }}
                >
                  {loadingConversations ? 'Chargement...' : 'Charger plus de conversations'}
                </Button>
              </Box>
            ) : null}
          </Box>
        </Box>

        {/* Chat */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: isMobile ? (mobileView === 'chat' ? 'flex' : 'none') : 'flex',
            flexDirection: 'column',
            minHeight: 0,
            background:
              'radial-gradient(1200px 600px at 100% 0%, rgba(37,99,235,0.06) 0%, rgba(255,255,255,1) 55%)',
          }}
        >
          <Box sx={{ p: 1.5, borderBottom: '1px solid #e9ecef', bgcolor: '#f8fafc' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                {isMobile ? (
                  <Tooltip title="Retour">
                    <IconButton
                      size="small"
                      onClick={() => setMobileView('list')}
                      sx={{ bgcolor: '#fff', border: '1px solid #e5e7eb' }}
                    >
                      <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}

                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: selectedConversation ? 'rgba(16,185,129,0.14)' : '#f3f4f6',
                    color: selectedConversation ? '#047857' : '#374151',
                    fontWeight: 900,
                    border: selectedConversation ? '1px solid rgba(16,185,129,0.22)' : '1px solid #e5e7eb',
                  }}
                >
                  {initialsFrom(selectedConversation?.userId ?? selectedConversation?.id)}
                </Avatar>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.15, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 950, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedConversation ? (selectedConversation.displayName || selectedConversation.userId || selectedConversation.id) : 'Sélectionne une conversation'}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', fontSize: 12, fontWeight: 700 }}>
                    {selectedConversation ? `${selectedConversation.userId || ''} • ${selectedConversation.channel || channel}` : '—'}
                  </Typography>
                </Box>
              </Box>

            </Box>
          </Box>

          {/* ── Load-more bar: outside the scroll area so it's always visible ── */}
          {hasMoreMessages ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 0.75,
                px: 2,
                borderBottom: '1px solid #e9ecef',
                bgcolor: 'rgba(37,99,235,0.04)',
                flexShrink: 0,
              }}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={loadMoreMessages}
                disabled={loadingMoreMessages}
                startIcon={loadingMoreMessages ? <CircularProgress size={14} /> : null}
                sx={{
                  borderRadius: 3,
                  fontWeight: 800,
                  fontSize: 12,
                  borderColor: 'rgba(37,99,235,0.35)',
                  color: '#1d4ed8',
                  bgcolor: '#fff',
                  boxShadow: '0 1px 4px rgba(37,99,235,0.08)',
                  '&:hover': { bgcolor: 'rgba(37,99,235,0.08)', borderColor: '#2563eb' },
                  px: 2.5,
                  py: 0.5,
                }}
              >
                {loadingMoreMessages ? 'Chargement...' : '⬆ Charger les messages précédents'}
              </Button>
            </Box>
          ) : null}

          <Box
            ref={messagesContainerRef}
            onScroll={handleMessagesScroll}
            sx={{
              flex: 1,
              overflow: 'auto', // Scroll uniquement dans la zone messages
              p: { xs: 1.25, md: 2 },
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              minHeight: 0,
            }}
          >
            {messages.map((m) => {
              const inbound = isInbound(m);
              const rowAlign = inbound ? 'flex-start' : 'flex-end';
              const bubbleBg = inbound ? '#ffffff' : '#E7FFEF';
              const bubbleBorder = inbound ? '#e5e7eb' : 'rgba(16, 185, 129, 0.25)';
              const imageReady = isMediaReady(m, m.imageUrl);
              const fileReady = isMediaReady(m, m.fileUrl);

              return (
                <Box key={m.id} sx={{ display: 'flex', justifyContent: rowAlign }}>
                  <Box
                    sx={{
                      maxWidth: { xs: '92%', md: '78%' },
                      background: bubbleBg,
                      border: `1px solid ${bubbleBorder}`,
                      borderRadius: 3,
                      p: 1.25,
                      boxShadow: inbound ? '0 1px 0 rgba(0,0,0,0.02)' : '0 10px 30px rgba(16,185,129,0.07)',
                    }}
                  >
                    {m.imageUrl ? (
                      imageReady ? (
                        <Box
                          component="img"
                          src={m.imageUrl}
                          alt="media"
                          onClick={() => setFullscreenImageUrl(m.imageUrl)}
                          sx={{
                            width: { xs: 160, md: 220 },
                            maxHeight: 160,
                            objectFit: 'cover',
                            borderRadius: 2,
                            display: 'block',
                            mb: m.text ? 1 : 0,
                            cursor: 'zoom-in',
                            border: '1px solid #e5e7eb',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: { xs: 160, md: 220 },
                            height: 120,
                            borderRadius: 2,
                            border: '1px solid #e5e7eb',
                            bgcolor: '#f8fafc',
                            mb: m.text ? 1 : 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            px: 1,
                          }}
                        >
                          <CircularProgress size={16} />
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }}>
                            Téléchargement image...
                          </Typography>
                        </Box>
                      )
                    ) : null}
                    {!m.imageUrl && m.fileUrl ? (
                      <Box sx={{ mb: m.text ? 1 : 0 }}>
                        <Button
                          size="small"
                          startIcon={fileReady ? <InsertDriveFileIcon /> : <CircularProgress size={14} />}
                          variant="outlined"
                          disabled={!fileReady}
                          component="a"
                          href={fileReady ? m.fileUrl : undefined}
                          target="_blank"
                          rel="noreferrer"
                          download={fileReady}
                        >
                          {fileReady ? (m.fileName || 'Télécharger le fichier') : 'Téléchargement fichier...'}
                        </Button>
                      </Box>
                    ) : null}
                    {m.text ? (
                      <Typography
                        sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontSize: 13,
                          fontWeight: 700,
                          color: '#0f172a',
                        }}
                      >
                        {m.text}
                      </Typography>
                    ) : null}
                    <Box sx={{ mt: 0.45, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 800 }}>
                        {formatTs(m.ts)}
                      </Typography>
                      {!inbound ? statusIconForMessage(m) : null}
                    </Box>
                  </Box>
                </Box>
              );
            })}

            {messages.length === 0 && !loadingMessages ? (
              <Typography sx={{ color: 'text.secondary', fontSize: 13, textAlign: 'center', py: 3 }}>
                Aucun message pour le moment.
              </Typography>
            ) : null}
            <div ref={endRef} />
          </Box>

          <Divider />

          <Box
            sx={{
              p: { xs: 1.25, md: 2 },
              display: 'flex',
              gap: 1.25,
              alignItems: 'flex-end',
              flexShrink: 0, // Toujours visible sans scroll
              bgcolor: '#fff',
              borderTop: '1px solid #e9ecef',
            }}
          >
            <input
              ref={mediaInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={onSelectImageFile}
            />
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={onSelectFile}
            />
            <Box
              sx={{
                flex: 1,
                border: selectedConversation ? '1px solid #e5e7eb' : '1px solid #f3f4f6',
                borderRadius: 3,
                background: '#fff',
                p: 1,
              }}
            >
              {selectedImagePreview ? (
                <Box
                  sx={{
                    mb: 1,
                    position: 'relative',
                    width: 'fit-content',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <Box
                    component="img"
                    src={selectedImagePreview}
                    alt="preview"
                    sx={{ maxWidth: 180, maxHeight: 120, display: 'block' }}
                  />
                  <IconButton
                    size="small"
                    onClick={removeSelectedImage}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: 'rgba(15,23,42,0.75)',
                      color: '#fff',
                      '&:hover': { bgcolor: 'rgba(15,23,42,0.9)' },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ) : null}
              {selectedFile ? (
                <Box
                  sx={{
                    mb: 1,
                    px: 1.2,
                    py: 0.8,
                    border: '1px solid #e5e7eb',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InsertDriveFileIcon fontSize="small" />
                    <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{selectedFile.name}</Typography>
                  </Box>
                  <IconButton size="small" onClick={removeSelectedFile}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ) : null}

              <Box
                component="textarea"
                ref={draftRef}
                placeholder="Tape ton message..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    onSend();
                  }
                }}
                sx={{
                  width: '100%',
                  minHeight: 46,
                  maxHeight: { xs: 120, md: 160 },
                  resize: 'vertical',
                  border: 'none',
                  outline: 'none',
                  p: 0.2,
                  fontFamily: 'inherit',
                  fontSize: 13,
                  fontWeight: 700,
                  background: 'transparent',
                }}
                disabled={!selectedConversation}
              />
              {String((selectedConversation?.channel || channel || '')).toLowerCase() === 'whatsapp' ? (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'text.secondary', minWidth: 66 }}>
                    Template
                  </Typography>
                  <Select
                    size="small"
                    value={selectedTemplateName}
                    onChange={(e) => setSelectedTemplateName(e.target.value)}
                    displayEmpty
                    disabled={!selectedConversation || sending || loadingTemplates}
                    sx={{ minWidth: 260, bgcolor: '#fff' }}
                  >
                    <MenuItem value="">Aucun (message normal)</MenuItem>
                    {whatsappTemplates.map((tpl) => (
                      <MenuItem key={tpl?.id || tpl?.name} value={tpl?.name || ''}>
                        {tpl?.name || 'Template sans nom'}
                      </MenuItem>
                    ))}
                  </Select>
                  {loadingTemplates ? <CircularProgress size={16} /> : null}
                </Box>
              ) : null}
            </Box>

            <Tooltip title="Joindre un média">
              <span>
                <IconButton
                  onClick={onOpenAttachMenu}
                  disabled={!selectedConversation || sending}
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: 3,
                    border: '1px solid #e5e7eb',
                    bgcolor: '#fff',
                  }}
                >
                  <AttachFileIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Menu
              anchorEl={attachMenuEl}
              open={Boolean(attachMenuEl)}
              onClose={onCloseAttachMenu}
            >
              <MenuItem onClick={onPickImageClick}>
                <ImageIcon sx={{ mr: 1 }} /> Image
              </MenuItem>
              <MenuItem onClick={onPickFileClick}>
                <InsertDriveFileIcon sx={{ mr: 1 }} /> Fichier
              </MenuItem>
            </Menu>
            <Tooltip title="Emoji">
              <span>
                <IconButton
                  onClick={onOpenEmojiMenu}
                  disabled={!selectedConversation || sending}
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: 3,
                    border: '1px solid #e5e7eb',
                    bgcolor: '#fff',
                  }}
                >
                  <EmojiEmotionsIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Menu anchorEl={emojiMenuEl} open={Boolean(emojiMenuEl)} onClose={onCloseEmojiMenu}>
              <Box sx={{ px: 1, py: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap', width: 220 }}>
                {EMOJIS.map((e) => (
                  <IconButton key={e} size="small" onClick={() => onPickEmoji(e)}>
                    <Typography sx={{ fontSize: 20 }}>{e}</Typography>
                  </IconButton>
                ))}
              </Box>
            </Menu>

            <Button
              variant="contained"
              onClick={onSend}
              disabled={sending || !selectedConversation}
              sx={{
                minWidth: { xs: 52, md: 128 },
                height: 46,
                borderRadius: 3,
                fontWeight: 950,
                background: 'linear-gradient(135deg, #10B981 0%, #2563eb 100%)',
                '&:hover': { filter: 'brightness(0.98)' },
              }}
              endIcon={isMobile ? undefined : <SendIcon />}
            >
              {isMobile ? <SendIcon fontSize="small" /> : (sending ? 'Envoi...' : 'Envoyer')}
            </Button>
          </Box>
          <Dialog
            open={Boolean(fullscreenImageUrl)}
            onClose={() => setFullscreenImageUrl('')}
            maxWidth="lg"
            fullWidth
          >
            <Box
              component="img"
              src={fullscreenImageUrl}
              alt="image plein ecran"
              sx={{
                width: '100%',
                maxHeight: '90vh',
                objectFit: 'contain',
                bgcolor: '#000',
              }}
            />
          </Dialog>
          <Dialog
            open={campaignModalOpen}
            onClose={() => setCampaignModalOpen(false)}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: 900 }}>Gestion campagnes WhatsApp</DialogTitle>
            <DialogContent dividers sx={{ p: 2 }}>
              <Typography sx={{ mt: 0.5, color: 'text.secondary', fontSize: 12 }}>
                Choisis un template WhatsApp puis un segment Mautic pour lancer la campagne.
              </Typography>

              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'text.secondary', minWidth: 66 }}>
                  Template
                </Typography>
                <Select
                  size="small"
                  value={selectedTemplateName}
                  onChange={(e) => setSelectedTemplateName(e.target.value)}
                  displayEmpty
                  disabled={loadingTemplates || launchingCampaign}
                  sx={{ minWidth: 260, bgcolor: '#fff' }}
                >
                  <MenuItem value="">Choisir un template</MenuItem>
                  {whatsappTemplates.map((tpl) => (
                    <MenuItem key={tpl?.id || tpl?.name} value={tpl?.name || ''}>
                      {tpl?.name || 'Template sans nom'}
                    </MenuItem>
                  ))}
                </Select>
                {loadingTemplates ? <CircularProgress size={16} /> : null}
              </Box>

              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'text.secondary', minWidth: 66 }}>
                  Segment
                </Typography>
                <Select
                  size="small"
                  value={campaignSegmentValue}
                  onChange={(e) => setCampaignSegmentValue(e.target.value)}
                  displayEmpty
                  disabled={launchingCampaign || loadingMauticSegments}
                  sx={{ minWidth: 340, maxWidth: 520, bgcolor: '#fff' }}
                >
                  <MenuItem value="">Choisir un segment Mautic</MenuItem>
                  {mauticSegments.map((seg) => (
                    <MenuItem key={seg?.id || seg?.name} value={String(seg?.id || '')}>
                      {seg?.name || 'Segment sans nom'}
                    </MenuItem>
                  ))}
                </Select>
                {loadingMauticSegments ? <CircularProgress size={16} /> : null}
                <Button
                  variant="contained"
                  onClick={onLaunchWhatsappCampaign}
                  disabled={launchingCampaign || loadingTemplates || loadingMauticSegments}
                  sx={{
                    height: 40,
                    borderRadius: 2,
                    fontWeight: 900,
                    background: 'linear-gradient(135deg, #10B981 0%, #2563eb 100%)',
                  }}
                >
                  {launchingCampaign ? 'Lancement...' : 'Lancer la campagne'}
                </Button>
              </Box>

              {campaignResult ? (
                <Alert severity="success" sx={{ mt: 1.5 }}>
                  {campaignResult?.message || 'Campagne lancee. Le traitement continue en arriere-plan.'}
                </Alert>
              ) : null}

              <Divider sx={{ my: 2 }} />
              <Typography sx={{ fontWeight: 900, fontSize: 14, mb: 1 }}>Historique des campagnes</Typography>
              {loadingCampaignHistory ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : null}
              {!loadingCampaignHistory && campaignHistory.length === 0 ? (
                <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
                  Aucun historique de campagne.
                </Typography>
              ) : null}
              {!loadingCampaignHistory && campaignHistory.map((run) => (
                <Box
                  key={run?.campaignId || `${run?.templateName}-${run?.createdAt}`}
                  sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 1.25, mb: 1 }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: 13 }}>
                    {run?.templateName || 'Template'} ({run?.templateLanguage || 'en'})
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', fontSize: 11, mb: 0.5 }}>
                    {formatTs(run?.createdAt)}
                  </Typography>
                  {run?.scheduledAt ? (
                    <Typography sx={{ color: '#0d9488', fontSize: 11, fontWeight: 600, mb: 0.5 }}>
                      Timing de lancement : {formatScheduledLaunchDisplay(run.scheduledAt)} (heure locale)
                    </Typography>
                  ) : null}
                  <Typography sx={{ fontSize: 12 }}>
                    KPI - Tentatives: {run?.summary?.attempted || 0}, Envoyes: {run?.summary?.sent || 0}, Recus: {run?.summary?.delivered || 0}, Lus: {run?.summary?.read || 0}, En attente: {run?.summary?.pending || 0}, Ignores: {run?.summary?.skipped || 0}, Erreurs: {run?.summary?.failed || 0}
                  </Typography>
                </Box>
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCampaignModalOpen(false)}>Fermer</Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Paper>
    </Box>
  );
}

